import { z } from 'zod';
import {
  CaseIdSchema,
  EmailSchema,
  InvitationIdSchema,
  IsoTimestampSchema,
  UserIdSchema,
} from './common.js';

// ---------------------------------------------------------------------------
// Roller
// ---------------------------------------------------------------------------

/** Applikasjonsnivå-rolle – gjelder globalt i CaseTrace */
export const AppRoleSchema = z.enum(['admin', 'user']);
export type AppRole = z.infer<typeof AppRoleSchema>;

/** Saknivå-rolle – gjelder per sak */
export const CaseRoleSchema = z.enum(['owner', 'member', 'viewer']);
export type CaseRole = z.infer<typeof CaseRoleSchema>;

// ---------------------------------------------------------------------------
// Bruker
// ---------------------------------------------------------------------------

export const UserSchema = z.object({
  id: UserIdSchema,
  email: EmailSchema,
  displayName: z.string().min(1, 'Visningsnavn kan ikke være tomt'),
  appRole: AppRoleSchema,
  disabled: z.boolean(),
});
export type User = z.infer<typeof UserSchema>;

// ---------------------------------------------------------------------------
// Saksmedlemskap
// ---------------------------------------------------------------------------

export const CaseMemberSchema = z.object({
  caseId: CaseIdSchema,
  userId: UserIdSchema,
  role: CaseRoleSchema,
  addedAt: IsoTimestampSchema,
  addedBy: UserIdSchema,
});
export type CaseMember = z.infer<typeof CaseMemberSchema>;

// ---------------------------------------------------------------------------
// Invitasjoner
// ---------------------------------------------------------------------------

export const InvitationStatusSchema = z.enum(['pending', 'accepted', 'revoked']);
export type InvitationStatus = z.infer<typeof InvitationStatusSchema>;

export const InvitationSchema = z.object({
  id: InvitationIdSchema,
  email: EmailSchema,
  caseId: CaseIdSchema,
  role: CaseRoleSchema,
  status: InvitationStatusSchema,
  invitedBy: UserIdSchema,
  createdAt: IsoTimestampSchema,
});
export type Invitation = z.infer<typeof InvitationSchema>;

// ---------------------------------------------------------------------------
// Input-skjemaer for admin-callables (auth-fundament, PR3)
// Brukes til å validere innkommende data på grensen (Cloud Functions).
// ---------------------------------------------------------------------------

/** Input til provisjonering/invitasjon av ny bruker – kun admin. */
export const ProvisionUserInputSchema = z.object({
  email: EmailSchema,
  displayName: z.string().min(1, 'Visningsnavn kan ikke være tomt'),
  appRole: AppRoleSchema.default('user'),
});
export type ProvisionUserInput = z.infer<typeof ProvisionUserInputSchema>;

/** Input til å sette app-rolle (custom claim) for en bruker – kun admin. */
export const SetAppRoleInputSchema = z.object({
  uid: UserIdSchema,
  role: AppRoleSchema,
});
export type SetAppRoleInput = z.infer<typeof SetAppRoleInputSchema>;
